import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { FileText, AlertTriangle, Loader2, ChevronRight } from "lucide-react";

const MONTHS = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];

// Timestamp relativo in italiano
function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "adesso";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min fa`;
  if (diffSec < 86400) {
    const h = Math.floor(diffSec / 3600);
    return `${h} ${h === 1 ? "ora" : "ore"} fa`;
  }
  const days = Math.floor(diffSec / 86400);
  if (days === 1) return "ieri";
  if (days < 7) return `${days} giorni fa`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks} ${weeks === 1 ? "settimana" : "settimane"} fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

export default function RelazioniFeedWidget() {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    api.get("/monthly-reports/feed?limit=10")
      .then((r) => { if (mounted) setFeed(r.data || []); })
      .catch(() => { if (mounted) setFeed([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const openReport = async (id) => {
    // fire-and-forget mark viewed
    api.post(`/monthly-reports/${id}/mark-viewed`).catch(() => {});
    navigate(`/relazioni/${id}`);
  };

  return (
    <div className="ih-glass rounded-2xl p-6" data-testid="feed-relazioni-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-lavender-600" />
          <h2 className="font-display text-lg tracking-tight">Relazioni ricevute</h2>
        </div>
        <button
          onClick={() => navigate("/relazioni?stato=inviata")}
          className="text-xs text-lavender-600 hover:underline"
          data-testid="feed-see-all"
        >
          Vedi tutte
        </button>
      </div>

      {loading ? (
        <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin inline text-lavender-500" /></div>
      ) : feed.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          Nessuna relazione ricevuta di recente.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {feed.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => openReport(r.id)}
                className="w-full rounded-xl border border-border/60 p-3 bg-background/40 hover:bg-accent/30 transition-colors text-left flex items-center gap-3"
                data-testid={`feed-item-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{r.hotel_nome}</span>
                    <span className="text-xs text-muted-foreground">·</span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {MONTHS[r.mese - 1]} {r.anno}
                    </span>
                    {!r.visualizzata_dal_supervisor && (
                      <span
                        className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                        data-testid={`feed-new-${r.id}`}
                      >
                        Nuova
                      </span>
                    )}
                    {r.has_critical && (
                      <AlertTriangle
                        className="h-3.5 w-3.5 text-rose-500"
                        title="KPI chiave sotto obiettivo"
                        data-testid={`feed-critical-${r.id}`}
                      />
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Manager: {r.manager_nome} · {relativeTime(r.inviata_at)}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
